import { useState } from 'react';
import { useI18n } from '../../i18n';
import { useSEO } from '../../hooks/useSEO';

interface JwtPayload {
    [key: string]: unknown;
}

interface DecodedJwt {
    header: JwtPayload;
    payload: JwtPayload;
    signature: string;
}

export default function JwtDecoder() {
    const { t } = useI18n();
    const [input, setInput] = useState('');
    const [decoded, setDecoded] = useState<DecodedJwt | null>(null);
    const [error, setError] = useState('');

    useSEO({
        title: t.tools.jwt.name,
        description: t.tools.jwt.description,
        keywords: ['JWT', 'JSON Web Token', 'decoder', 'parser', 'token', 'authentication'],
    });

    const decodeJwt = (token: string) => {
        try {
            setError('');

            if (!token.trim()) {
                setDecoded(null);
                return;
            }

            const parts = token.split('.');

            if (parts.length !== 3) {
                throw new Error(t.tools.jwt.invalidFormat);
            }

            // Decode header
            const header = JSON.parse(atob(parts[0]!.replace(/-/g, '+').replace(/_/g, '/')));

            // Decode payload
            const payload = JSON.parse(atob(parts[1]!.replace(/-/g, '+').replace(/_/g, '/')));

            // Signature (we don't decode it, just show it)
            const signature = parts[2]!;

            setDecoded({
                header,
                payload,
                signature,
            });
        } catch (err) {
            setError(err instanceof Error ? err.message : t.tools.jwt.invalidToken);
            setDecoded(null);
        }
    };

    const handleInputChange = (value: string) => {
        setInput(value);
        decodeJwt(value);
    };

    const formatTimestamp = (timestamp: number) => {
        const date = new Date(timestamp * 1000);
        return date.toLocaleString();
    };

    const isExpired = (exp?: number) => {
        if (!exp) return false;
        return exp * 1000 < Date.now();
    };

    return (
        <div>
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    {t.tools.jwt.name}
                </h1>
                <p className="text-gray-600">{t.tools.jwt.description}</p>
            </div>

            <div className="space-y-6">
                {/* Input */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t.tools.jwt.inputToken}
                    </label>
                    <textarea
                        value={input}
                        onChange={(e) => handleInputChange(e.target.value)}
                        placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c"
                        className="input-field font-mono text-sm"
                        rows={4}
                    />
                </div>

                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <p className="text-red-700 text-sm">
                            <span className="font-semibold">{t.tools.jsonFormatter.error}:</span> {error}
                        </p>
                    </div>
                )}

                {decoded && (
                    <div className="space-y-4">
                        {/* Header */}
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900 mb-2">
                                {t.tools.jwt.header}
                            </h2>
                            <div className="bg-gray-50 rounded-lg p-4">
                                <pre className="text-sm overflow-x-auto">
                                    {JSON.stringify(decoded.header, null, 2)}
                                </pre>
                            </div>
                        </div>

                        {/* Payload */}
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900 mb-2">
                                {t.tools.jwt.payload}
                            </h2>
                            <div className="bg-gray-50 rounded-lg p-4">
                                <pre className="text-sm overflow-x-auto">
                                    {JSON.stringify(decoded.payload, null, 2)}
                                </pre>
                            </div>

                            {/* Standard Claims */}
                            {(() => {
                                const iat = decoded.payload.iat as number | undefined;
                                const exp = decoded.payload.exp as number | undefined;
                                const nbf = decoded.payload.nbf as number | undefined;
                                const hasTimeClaims = iat || exp || nbf;

                                if (!hasTimeClaims) return null;

                                return (
                                    <div className="mt-4 space-y-2">
                                        <h3 className="font-medium text-gray-700">{t.tools.jwt.claims}</h3>
                                        {iat && (
                                            <p className="text-sm text-gray-600">
                                                <span className="font-medium">Issued At (iat):</span>{' '}
                                                {formatTimestamp(iat)}
                                            </p>
                                        )}
                                        {exp && (
                                            <p className="text-sm">
                                                <span className="font-medium">Expires At (exp):</span>{' '}
                                                <span className={isExpired(exp) ? 'text-red-600' : 'text-green-600'}>
                                                    {formatTimestamp(exp)}
                                                    {isExpired(exp) && ' (⚠️ Expired)'}
                                                </span>
                                            </p>
                                        )}
                                        {nbf && (
                                            <p className="text-sm text-gray-600">
                                                <span className="font-medium">Not Before (nbf):</span>{' '}
                                                {formatTimestamp(nbf)}
                                            </p>
                                        )}
                                    </div>
                                );
                            })()}
                        </div>                        {/* Signature */}
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900 mb-2">
                                {t.tools.jwt.signature}
                            </h2>
                            <div className="bg-gray-50 rounded-lg p-4">
                                <pre className="text-sm overflow-x-auto font-mono break-all">
                                    {decoded.signature}
                                </pre>
                            </div>
                        </div>
                    </div>
                )}

                {/* Security Notice */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h3 className="font-semibold text-yellow-900 mb-2">
                        ⚠️ {t.tools.jwt.securityNotice}
                    </h3>
                    <p className="text-sm text-yellow-800">
                        {t.tools.jwt.securityDesc}
                    </p>
                </div>

                {/* About JWT */}
                <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                    <h3 className="font-semibold text-primary-900 mb-2">
                        {t.tools.jwt.aboutTitle}
                    </h3>
                    <div className="text-sm text-primary-800 space-y-1">
                        <p>• {t.tools.jwt.aboutDesc1}</p>
                        <p>• {t.tools.jwt.aboutDesc2}</p>
                        <p>• {t.tools.jwt.aboutDesc3}</p>
                    </div>
                </div>
            </div>
        </div>
    );
}
